'use client';
import React, { useState } from 'react';
import classNames from 'classnames';
import { NewsCardItem } from './NewsCardItem';
import { NewsBlockWithFilters } from './NewsBlockWithFilters';
import { RecentNewsBlock } from './RecentNewsBlock';
import styled from '@benzinga/themetron';
import { WordpressPost } from '@benzinga/content-manager';

export interface NewsWithTabsProps {
  article_tabs: ArticleTab[];
}

export interface ArticleTab {
  tab_title: string;
  tab_content: NewsWithTabsTabContent;
}

export interface NewsWithTabsTabContent {
  articles_list: {
    nodes?: WordpressPost[];
    tab_title: string;
    limit: string;
    vertical_id: number;
  }[];
  news_list: {
    limit: string;
    nodes?: WordpressPost[];
    primary_vertical_id: number;
  };
}

export const NewsWithTabs = ({ article_tabs }: NewsWithTabsProps) => {
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [activeArticlesTabIndex, setActiveArticlesTabIndex] = useState<number>(0);

  if (!article_tabs || !Array.isArray(article_tabs)) return null;

  const clickHandler = (index: number) => () => {
    if (activeTabIndex !== index) setActiveTabIndex(index);
  };

  return (
    <NewsWithTabsWrapper>
      <div className="tabs-wrapper">
        {Array.isArray(article_tabs) &&
          article_tabs.length &&
          article_tabs.map((tab, i) => (
            <button
              className={classNames('tab-item', { active: i === activeTabIndex })}
              key={i}
              onClick={clickHandler(i)}
            >
              {tab.tab_title}
            </button>
          ))}
      </div>
      <div className="articles-wrapper">
        {article_tabs[activeTabIndex]?.tab_content?.news_list?.nodes?.[0] ? (
          <NewsCardItem
            card={article_tabs[activeTabIndex]?.tab_content?.news_list?.nodes?.[0]}
            className="main-card-wrapper"
          />
        ) : null}
        <NewsBlockWithFilters
          activeIndex={activeArticlesTabIndex}
          className="cards-list-wrapper"
          list={article_tabs[activeTabIndex]?.tab_content?.articles_list}
          setActiveIndex={setActiveArticlesTabIndex}
        />
        {article_tabs[activeTabIndex]?.tab_content?.news_list?.nodes?.length && (
          <RecentNewsBlock
            blocks={article_tabs[activeTabIndex].tab_content.news_list.nodes?.slice(1, 4)}
            key={article_tabs[activeTabIndex].tab_content.news_list.primary_vertical_id + activeTabIndex}
          />
        )}
      </div>
    </NewsWithTabsWrapper>
  );
};

const NewsWithTabsWrapper = styled.div`
  &.news-with-tabs-wrapper {
    margin: 2rem 0;
    p {
      margin: 0;
    }
  }

  .tabs-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;

    @media screen and (max-width: 959px) {
      overflow-x: auto;
      -ms-overflow-style: none; /* for Internet Explorer, Edge */
      scrollbar-width: none; /* for Firefox */
    }

    &::-webkit-scrollbar {
      display: none; /* for Chrome, Safari, and Opera */
    }
  }

  .tab-item {
    flex: 1;
    border: 1px solid #ceddf2;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 16px;
    font-weight: 700;
    font-size: 14px;
    color: #5b7292;
    white-space: nowrap;
    transition:
      0.25s background-color ease-in-out,
      0.25s color ease-in-out,
      0.25s border-color ease-in-out;

    &:not(.active):hover {
      color: #3f83f8;
      border-color: currentColor;
    }
  }

  .active {
    background-color: #3f83f8;
    border-color: #3f83f8;
    color: #ffffff;
    cursor: default;
  }

  .articles-wrapper {
    margin-top: 16px;
    display: flex;
    align-items: stretch;
    gap: 16px;
    flex-wrap: wrap;

    @media screen and (max-width: 959px) {
      flex-direction: column;
    }
  }

  .main-card-wrapper {
    flex: 2 1;
  }

  .cards-list-wrapper {
    flex: 1 0;
  }

  .recentCards-wrapper {
    flex: 1;
  }

  .recentCard {
    flex: 1;
    padding: 24px;
    background: #f2f8ff;
    border: 1px solid #e1ebfa;
    border-radius: 4px;
  }
`;
